import React, { useRef, useState } from "react";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { AssignLoadDialog } from "@/components/AssignLoadDialog";
import { Sparkle, FileArrowUp, Robot, CheckCircle, Truck } from "@phosphor-icons/react";
import { LoadBreakdownForm, LoadBreakdownView } from "@/components/LoadBreakdown";
import { AiCompletionPanel, missingLoadFields } from "@/components/AiCompletionPanel";
import { toast } from "sonner";


export default function AsterismAI() {
  const invalidate = useInvalidate();
  const fileRef = useRef(null);
  const { data: carriers = [] } = useList("carriers", "/carriers");
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [load, setLoad] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const submittingRef = React.useRef(false);


  const extract = async () => {
    if (!file) { toast.error("Upload a rate confirmation first"); return; }
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ai/ratecon/extract", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setLoad({ ...data.load, dead_head_miles: data.load.dead_head });
      toast.success("Asterism AI filled in the load details — review below");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Extraction failed"); }
    finally { setExtracting(false); }
  };

  const createLoad = async ({ carrierId, driverId, deadHead, from }) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setCreating(true);
    try {
      const dhm = Number(deadHead) || 0, loaded = Number(load.loaded_miles) || 0, total = +(loaded + dhm).toFixed(1), rate = Number(load.rate) || 0;
      const full = { ...load, dead_head: dhm, dead_head_miles: dhm, dead_head_from: from, total_miles: total, rpm: total && rate ? +(rate / total).toFixed(2) : load.rpm };
      const { data } = await api.post("/ai/ratecon/create-load", { load: full, carrier_id: carrierId, driver_id: driverId });
      toast.success(`Load ${data.load.reference} assigned to ${carriers.find((c) => c.id === carrierId)?.carrier_name || "carrier"} · ${data.load.driver || "driver"} · ${dhm} mi dead head`);
      invalidate(["loads", "dashboard"]);
      setAssignOpen(false); setLoad(null); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not create load"); }
    finally { submittingRef.current = false;  setCreating(false); }
  };

  const inp = "bg-white border-slate-300";

  return (
    <div>
      <PageHeader title="Asterism AI" subtitle="Upload a rate confirmation — AI reads the PDF and fills in the load, then one click creates it" />

      {!load && (
        <Panel className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-blue-600 grid place-items-center text-white"><Robot size={24} weight="duotone" /></div>
            <div>
              <p className="font-heading font-bold text-slate-900">Rate Confirmation Reader</p>
              <p className="text-sm text-slate-500">Vision AI reads every page — even blurry scans, faxes and phone photos</p>
            </div>
          </div>
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
            className="border-2 border-dashed border-slate-300 rounded-2xl p-12 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors"
            data-testid="ai-dropzone"
          >
            <div className="mx-auto w-16 h-16 rounded-2xl bg-blue-600 text-white grid place-items-center mb-4"><FileArrowUp size={30} weight="duotone" /></div>
            <p className="font-heading font-bold text-slate-900 text-lg">{file ? file.name : "Drop a rate confirmation or click to upload"}</p>
            <p className="text-sm text-slate-500 mt-1">PDF (typed or scanned) · Photos & screenshots · Word · Email (.eml) · Excel · CSV · Text</p>
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.gif,.bmp,.tif,.tiff,.docx,.eml,.txt,.csv,.xlsx,.xls,image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); }} data-testid="ai-file-input" />
          </div>
          <div className="flex justify-center mt-6">
            <Button onClick={extract} disabled={!file || extracting} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold h-11 px-8" data-testid="ai-extract-btn">
              <Sparkle size={18} weight="fill" className="mr-2" />{extracting ? "Asterism AI is reading…" : "Read with Asterism AI"}
            </Button>
          </div>
        </Panel>
      )}

      {load && (
        <>
          <div className="flex items-center justify-between mb-3">
            <span className="inline-flex items-center gap-1.5 text-slate-700 font-semibold text-sm"><CheckCircle size={16} className="text-emerald-500" weight="fill" />Asterism AI built the load breakdown. Review or edit, then assign a carrier</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setLoad(null); }} className="border-slate-300 bg-white text-slate-700">Start Over</Button>
              <Button onClick={() => setAssignOpen(true)} disabled={missingLoadFields(load).length > 0} className="bg-emerald-600 text-white hover:bg-emerald-700 font-semibold" data-testid="ai-create-load-btn"><Truck size={16} className="mr-1.5" />Next: Assign Carrier</Button>
            </div>
          </div>
          <AiCompletionPanel load={load} setLoad={setLoad} />
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_440px] gap-4 items-start">
            <Panel className="p-5"><LoadBreakdownForm value={load} onChange={setLoad} hideDriver /></Panel>
            <div className="xl:sticky xl:top-4"><LoadBreakdownView load={load} compact /></div>
          </div>
        </>
      )}

      <AssignLoadDialog open={assignOpen} onOpenChange={setAssignOpen} load={load} carriers={carriers} creating={creating} onAssign={createLoad} />
    </div>
  );
}
